import { useState, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';

// Types for the email interface
export interface EmailMessage {
  id: string;
  from: string;
  to: string;
  subject: string;
  body: string;
  receivedAt: Date;
  isRead: boolean;
  labels?: string[];
  attachments?: Attachment[];
}

export interface Attachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
}

export interface EmailFolder {
  id: string;
  name: string;
  unreadCount: number;
  totalCount: number;
}

export interface UseEmailAPIResult {
  // State
  emails: EmailMessage[];
  folders: EmailFolder[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchEmails: (folderId?: string, limit?: number) => Promise<EmailMessage[]>;
  fetchFolders: () => Promise<EmailFolder[]>;
  markAsRead: (emailId: string) => Promise<boolean>;
  deleteEmail: (emailId: string) => Promise<boolean>;
  archiveEmail: (emailId: string) => Promise<boolean>;
  moveEmail: (emailId: string, destinationFolderId: string) => Promise<boolean>;
  searchEmails: (query: string) => Promise<EmailMessage[]>;
}

export function useEmailAPI(): UseEmailAPIResult {
  const { user, getProviderToken, refreshProviderToken } = useAuth();
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [folders, setFolders] = useState<EmailFolder[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Add this to track if a token refresh is in progress
  const isRefreshing = useRef(false);
  // Track if initial data has been loaded
  const initialDataLoaded = useRef(false);

  // Determine email provider from user metadata or email domain
  const getEmailProvider = (): 'google' | 'microsoft' | null => {
    if (!user) return null;
    
    // Check if provider info is in user metadata
    const provider = user.app_metadata?.provider as string;
    if (provider) {
      if (provider === 'google') return 'google';
      if (provider === 'azure') return 'microsoft';
    }
    
    // Fallback to checking email domain
    const email = user.email || '';
    if (email.includes('gmail') || email.includes('googlemail')) return 'google';
    if (email.includes('outlook') || email.includes('hotmail') || email.includes('live') || email.includes('msn')) return 'microsoft';
    
    return null;
  };

  // Get token and handle refresh if needed - now with refresh prevention
  const getTokenWithRetry = async (): Promise<string | null> => {
    let token = await getProviderToken();
    
    if (!token && !isRefreshing.current) {
      isRefreshing.current = true;
      console.log("No token found, attempting to refresh...");
      token = await refreshProviderToken();
      isRefreshing.current = false;
    }
    
    return token;
  };

  // Fetch emails from Gmail API
  const fetchGmailEmails = async (folderId: string = 'INBOX', limit: number = 20): Promise<EmailMessage[]> => {
    const token = await getTokenWithRetry();
    if (!token) throw new Error('No provider token available');

    console.log(`Fetching Gmail emails from folder: ${folderId}`);
    
    const labelId = folderId === 'INBOX' ? 'INBOX' : folderId;
    const url = `https://gmail.googleapis.com/gmail/v1/users/me/messages?labelIds=${labelId}&maxResults=${limit}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Gmail API error:", errorData);
      throw new Error(`Failed to fetch emails: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`Received ${data.messages?.length || 0} messages from Gmail API`);
    
    const messageIds = data.messages || [];
    
    if (messageIds.length === 0) {
      return [];
    }
    
    // Fetch detailed message data for each message
    const emailPromises = messageIds.map(async (message: { id: string }) => {
      const msgResponse = await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages/${message.id}?format=metadata`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!msgResponse.ok) {
        throw new Error(`Failed to fetch message details: ${msgResponse.statusText}`);
      }

      const msgData = await msgResponse.json();
      
      // Parse Gmail message format to our standardized format
      const headers = msgData.payload.headers || [];
      const subject = headers.find((h: any) => h.name === 'Subject')?.value || '(No subject)';
      const from = headers.find((h: any) => h.name === 'From')?.value || '';
      const to = headers.find((h: any) => h.name === 'To')?.value || '';
      
      // Gmail returns internal date as seconds since epoch
      const internalDate = msgData.internalDate ? parseInt(msgData.internalDate) : Date.now();
      const date = new Date(internalDate);
      
      // Extract body content (simplified version)
      let body = '';
      if (msgData.payload.body?.data) {
        body = atob(msgData.payload.body.data.replace(/-/g, '+').replace(/_/g, '/'));
      } else if (msgData.payload.parts && msgData.payload.parts.length) {
        // Find text part
        const textPart = msgData.payload.parts.find((part: any) => part.mimeType === 'text/plain');
        if (textPart && textPart.body?.data) {
          body = atob(textPart.body.data.replace(/-/g, '+').replace(/_/g, '/'));
        }
      }

      // Check if message is read
      const isRead = !msgData.labelIds.includes('UNREAD');
      
      return {
        id: msgData.id,
        from,
        to,
        subject,
        body,
        receivedAt: date,
        isRead,
        labels: msgData.labelIds || []
      };
    });

    try {
      const emails = await Promise.all(emailPromises);
      return emails;
    } catch (error) {
      console.error("Error fetching email details:", error);
      throw error;
    }
  };

  // Fetch emails from Outlook API
  const fetchOutlookEmails = async (folderId: string = 'inbox', limit: number = 20): Promise<EmailMessage[]> => {
    const token = await getTokenWithRetry();
    if (!token) throw new Error('No provider token available');

    const url = `https://graph.microsoft.com/v1.0/me/mailFolders/${folderId}/messages?$top=${limit}&$orderby=receivedDateTime desc`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Microsoft Graph API error:", errorData);
      throw new Error(`Failed to fetch emails: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Map Outlook message format to our standardized format
    return data.value.map((msg: any) => ({
      id: msg.id,
      from: msg.from.emailAddress.address,
      to: msg.toRecipients.map((r: any) => r.emailAddress.address).join(', '),
      subject: msg.subject || '(No subject)',
      body: msg.bodyPreview || '',
      receivedAt: new Date(msg.receivedDateTime),
      isRead: msg.isRead,
      attachments: msg.hasAttachments ? [] : undefined // We'd need another API call to get attachment details
    }));
  };

  // Fetch folders from Gmail API
  const fetchGmailFolders = async (): Promise<EmailFolder[]> => {
    const token = await getTokenWithRetry();
    if (!token) throw new Error('No provider token available');

    console.log("Fetching Gmail folders");
    
    const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/labels', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Gmail API error:", errorData);
      throw new Error(`Failed to fetch folders: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`Received ${data.labels?.length || 0} labels from Gmail API`);
    
    // Filter to system labels that make sense as folders
    const systemLabels = ['INBOX', 'SENT', 'DRAFT', 'TRASH', 'SPAM'];
    const folderLabels = data.labels.filter((label: any) => 
      systemLabels.includes(label.id) || label.type === 'user'
    );
    
    // Map to our folder format
    return folderLabels.map((label: any) => ({
      id: label.id,
      name: label.name,
      unreadCount: label.messagesUnread || 0,
      totalCount: label.messagesTotal || 0
    }));
  };

  // Generic API methods that call provider-specific implementations
  const fetchEmails = async (folderId?: string, limit?: number): Promise<EmailMessage[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const provider = getEmailProvider();
      if (!provider) throw new Error('Email provider not detected');
      
      let results: EmailMessage[] = [];
      
      if (provider === 'google') {
        results = await fetchGmailEmails(folderId, limit);
      } else if (provider === 'microsoft') {
        results = await fetchOutlookEmails(folderId, limit);
      } else {
        throw new Error('Unsupported email provider');
      }
      
      setEmails(results);
      return results;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch emails';
      console.error("Error in fetchEmails:", errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async (): Promise<EmailFolder[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const provider = getEmailProvider();
      if (!provider) throw new Error('Email provider not detected');
      
      let results: EmailFolder[] = [];
      
      if (provider === 'google') {
        results = await fetchGmailFolders();
      } else if (provider === 'microsoft') {
        results = []; // Implement fetchOutlookFolders when needed
      } else {
        throw new Error('Unsupported email provider');
      }
      
      setFolders(results);
      return results;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch folders';
      console.error("Error in fetchFolders:", errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Mark email as read (stub implementation)
  const markAsRead = async (emailId: string): Promise<boolean> => {
    // Implementation goes here
    return true;
  };

  // Delete email (stub implementation)
  const deleteEmail = async (emailId: string): Promise<boolean> => {
    // Implementation goes here
    return true;
  };

  // Archive email (stub implementation)
  const archiveEmail = async (emailId: string): Promise<boolean> => {
    // Implementation goes here
    return true;
  };

  // Move email to another folder (stub implementation)
  const moveEmail = async (emailId: string, destinationFolderId: string): Promise<boolean> => {
    // Implementation goes here
    return true;
  };

  // Search emails (stub implementation)
  const searchEmails = async (query: string): Promise<EmailMessage[]> => {
    // Implementation goes here
    return [];
  };

  // Load initial data when user changes - with safeguards
  useEffect(() => {
    const loadInitialData = async () => {
      if (user && !initialDataLoaded.current && !loading) {
        console.log("Loading initial email data");
        initialDataLoaded.current = true;
        
        try {
          setLoading(true);
          await fetchFolders();
          await fetchEmails();
        } catch (err) {
          console.error('Failed to load initial email data:', err);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadInitialData();
    
    // Cleanup function to reset the initial data flag when user changes
    return () => {
      if (!user) {
        initialDataLoaded.current = false;
      }
    };
  }, [user]);

  return {
    emails,
    folders,
    loading,
    error,
    fetchEmails,
    fetchFolders,
    markAsRead,
    deleteEmail,
    archiveEmail,
    moveEmail,
    searchEmails
  };
}